import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { getVendorAvailability } from '@/api';
import { resolveGqlErrors } from '@/helpers';

import type { ProductType, VendorAvailabilityData } from '../types';

type UseAvailabilityParams = {
  vendorId: string;
  productType: ProductType;
  month: number | null;
  year: number | null;
};

type UseAvailability = {
  loading: boolean;
  error: string;
  data: VendorAvailabilityData | null;
  refetch: () => void;
};

export const useAvailability = ({
  vendorId,
  productType,
  month,
  year,
}: UseAvailabilityParams): UseAvailability => {
  const { loading, data, error, refetch } = useQuery(getVendorAvailability, {
    variables: {
      input: { vendorId, productType, month: month || 0, year: year || 0 },
    },
    skip: !month || !year,
    fetchPolicy: 'cache-first',
  });

  const availability = data?.getVendorAvailability?.availability ?? null;

  const mapped = useMemo<VendorAvailabilityData | null>(
    () =>
      availability
        ? {
            dates: availability.dates.map((d) => ({ date: d.date, available: d.available })),
            mealPeriods: availability.mealPeriods
              ? {
                  breakfast: availability.mealPeriods.breakfast,
                  lunch: availability.mealPeriods.lunch,
                  dinner: availability.mealPeriods.dinner,
                  appetizers: availability.mealPeriods.appetizers,
                }
              : null,
            eligibleMealPeriods: availability.eligibleMealPeriods
              ? {
                  breakfast: availability.eligibleMealPeriods.breakfast,
                  lunch: availability.eligibleMealPeriods.lunch,
                  dinner: availability.eligibleMealPeriods.dinner,
                  appetizers: availability.eligibleMealPeriods.appetizers,
                }
              : null,
            submission: availability.submission
              ? {
                  submittedAt: availability.submission.submittedAt ?? '',
                  submittedByEmail: availability.submission.submittedByEmail ?? '',
                  version: availability.submission.version ?? 0,
                }
              : null,
            submissionStatus: availability.submissionStatus,
            windowStatus: availability.windowStatus,
            deadlineDate: availability.deadlineDate ?? null,
            requiresSubmission: availability.requiresSubmission,
          }
        : null,
    [availability]
  );

  return {
    loading,
    error: resolveGqlErrors(error || data?.getVendorAvailability?.error),
    data: mapped,
    refetch,
  };
};
